import { useState, useEffect } from 'react';
import { requestNotificationPermission } from '@/services/notifications';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faBellSlash,
  faXmark
} from '@fortawesome/free-solid-svg-icons';

const STORAGE_KEY = 'verigit-notification-prompt-dismissed';

interface NotificationPermissionDialogProps {
  isDark: boolean;
}

export function NotificationPermissionDialog({ isDark }: NotificationPermissionDialogProps) {
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    // Don't show if:
    // - Notifications not supported
    // - Already granted or denied
    // - User previously dismissed
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'default') return;
    if (localStorage.getItem(STORAGE_KEY)) return;

    // Show after a short delay so the page settles
    const timer = setTimeout(() => {
      setVisible(true);
      requestAnimationFrame(() => setAnimating(true));
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  const handleAllow = async () => {
    setAnimating(false);
    setTimeout(() => setVisible(false), 300);
    await requestNotificationPermission();
  };

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setAnimating(false);
    setTimeout(() => setVisible(false), 300);
  };

  if (!visible) return null;

  return (
    <div className={`fixed inset-0 z-[9999] flex items-center justify-center transition-all duration-300 ${animating ? 'opacity-100' : 'opacity-0'}`}>
      {/* Backdrop */}
      <div
        className={`absolute inset-0 transition-all duration-300 ${animating ? 'backdrop-blur-sm' : ''} ${isDark ? 'bg-black/40' : 'bg-black/20'}`}
        onClick={handleDismiss}
      />

      {/* Dialog */}
      <div
        className={`relative w-[380px] overflow-hidden rounded-2xl shadow-2xl transition-all duration-300 ${
          animating ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
        }`}
      >
        {/* Rainbow border glow */}
        <div
          className="absolute inset-0 rounded-2xl p-[1px]"
          style={{
            background: 'linear-gradient(135deg, #fb7185, #d946ef, #8b5cf6, #3b82f6, #22d3ee, #34d399, #fbbf24)',
            backgroundSize: '200% 200%',
            animation: 'rainbow-shift 4s ease infinite',
          }}
        >
          <div className={`absolute inset-[1px] rounded-2xl ${isDark ? 'bg-slate-900/95' : 'bg-white/95'}`} />
        </div>

        {/* Content */}
        <div className="relative z-10 p-6">
          {/* Close button */}
          <button
            onClick={handleDismiss}
            className={`absolute top-3 right-3 p-1 rounded-full transition-colors ${
              isDark ? 'text-slate-500 hover:text-slate-300 hover:bg-slate-800' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
            }`}
          >
            <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
          </button>

          {/* Icon with rainbow ring */}
          <div className="flex justify-center mb-4">
            <div className="relative">
              <div
                className="absolute inset-0 rounded-full blur-lg opacity-40 animate-pulse"
                style={{ background: 'linear-gradient(135deg, #fb7185, #d946ef, #8b5cf6, #3b82f6, #22d3ee, #34d399)' }}
              />
              <div className={`relative h-16 w-16 rounded-full flex items-center justify-center ${
                isDark
                  ? 'bg-gradient-to-br from-slate-800 to-slate-900 ring-1 ring-white/10'
                  : 'bg-gradient-to-br from-white to-slate-50 ring-1 ring-slate-200'
              }`}>
                <FontAwesomeIcon icon={faBell} className={`h-7 w-7 ${isDark ? 'text-cyan-400' : 'text-blue-500'}`} />
              </div>
            </div>
          </div>

          {/* Title */}
          <h3 className={`text-center text-lg font-semibold mb-1.5 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Stay in the loop
          </h3>

          {/* Description */}
          <p className={`text-center text-sm mb-5 leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            Get notified when analyses finish so you can switch tabs and come back when your data is ready.
          </p>

          {/* Buttons */}
          <div className="flex gap-2.5">
            <button
              onClick={handleDismiss}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                isDark
                  ? 'bg-slate-800 hover:bg-slate-700 text-slate-300 ring-1 ring-slate-700'
                  : 'bg-slate-100 hover:bg-slate-200 text-slate-600 ring-1 ring-slate-200'
              }`}
            >
              <FontAwesomeIcon icon={faBellSlash} className="h-4 w-4" />
              Not now
            </button>
            <button
              onClick={handleAllow}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-white transition-all bg-gradient-to-r from-blue-500 via-cyan-500 to-emerald-500 hover:from-blue-600 hover:via-cyan-600 hover:to-emerald-600 shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40"
            >
              <FontAwesomeIcon icon={faBell} className="h-4 w-4" />
              Enable
            </button>
          </div>

          {/* Fine print */}
          <p className={`text-center text-[10px] mt-3 ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
            You can change this anytime in your browser settings
          </p>
        </div>
      </div>

      {/* Keyframe animation for rainbow gradient shift */}
      <style>{`
        @keyframes rainbow-shift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
    </div>
  );
}
